import { useNavigate } from 'react-router-dom';
import { FileText, QrCode, Mail, Zap } from 'lucide-react';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Button } from '@/shared/ui/Button';
import { CTABanner } from '@/shared/ui/CTABanner';
import { SectionLabel } from '@/shared/ui/SectionLabel';
import { ClickableCard } from '@/shared/ui/ClickableCard';
import { Badge } from '@/shared/ui/Badge';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './MailersPage.module.css';

type MailerStatus = 'draft' | 'review' | 'production' | 'delivered';

interface Mailer {
  id: string;
  name: string;
  segment: string;
  qty: number;
  status: MailerStatus;
  responses: number;
  linked?: boolean;
}

const MAILERS: Mailer[] = [
  {
    id: 'imp',
    name: 'Implant Awareness — 55+ affluent',
    segment: 'Linked to: Dental Implants Authority',
    qty: 900,
    status: 'production',
    responses: 12,
    linked: true,
  },
  {
    id: 'b2s',
    name: 'Back-to-School Family Checkups',
    segment: 'Standalone · Families segment',
    qty: 1500,
    status: 'draft',
    responses: 0,
  },
];

const STATUS_LABELS: Record<MailerStatus, string> = {
  draft: 'Draft',
  review: 'Ready for Review',
  production: 'Production',
  delivered: 'Delivered',
};

const STATUS_BADGE_VARIANT: Record<MailerStatus, 'neutral' | 'warning' | 'success'> = {
  draft: 'neutral',
  review: 'warning',
  production: 'success',
  delivered: 'success',
};

export function MailersPage() {
  const navigate = useNavigate();

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Mailers"
        subtitle="PracticeNova picks the audience from your Strategy segments, designs the piece, and tracks response. Click any mailer to set up creative, list, and budget."
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Mail size={14} aria-hidden />}
            onClick={() => navigate('/print-mail/mailers/new')}
          >
            New mailer
          </Button>
        }
      />

      <CTABanner
        title="1 new mailer recommended"
        subtitle="Based on your New Movers Strategy segment · standalone"
        heading='New movers in 94521 → "New to Concord? Meet your family dentist."'
        pills={['1,200 households (ACS)', 'EDDM postcard', 'Free new-patient exam', 'QR + landing page']}
        actions={[{ label: 'Open & review →', onClick: () => navigate('/print-mail/mailers/rec'), variant: 'primary' }]}
        actionsAlign="bottom"
      />

      <section className={patterns.cardList}>
        <SectionLabel label="Your mailers" />

        {MAILERS.map((m) => (
          <ClickableCard
            key={m.id}
            onClick={() => navigate(`/print-mail/mailers/${m.id}`)}
          >
            <div className={patterns.cardRow}>
              <div className={patterns.cardLeft}>
                <div className={patterns.cardIcon}>
                  <FileText size={18} aria-hidden />
                </div>
                <div>
                  <div className={patterns.cardName}>
                    {m.name}
                    {m.linked && (
                      <span className={patterns.pillSuccess}>
                        <Zap size={10} aria-hidden />
                        linked
                      </span>
                    )}
                  </div>
                  <div className={patterns.cardSub}>{m.segment}</div>
                  <div className={patterns.cardDetail}>
                    {m.qty.toLocaleString()} pieces · AI-targeted
                  </div>
                </div>
              </div>
              <div className={patterns.cardRight}>
                <div className={styles.responseCount}>
                  <div className={styles.responseValue}>
                    <span className={styles.responseIcon}><QrCode size={13} aria-hidden /></span>
                    {m.responses} responses
                  </div>
                  <div className={styles.responseLabel}>QR + page + tracked #</div>
                </div>
                <Badge variant={STATUS_BADGE_VARIANT[m.status]}>{STATUS_LABELS[m.status]}</Badge>
              </div>
            </div>
          </ClickableCard>
        ))}
      </section>

      <p className={patterns.sectionFootnote}>
        Same Strategy, different channel: mailers use your segments but execute geographically.
        Targeting from Census/Places (no PMS).
      </p>
    </div>
  );
}
